import { getBackendUrl } from "@Utils";
import { isInfraFailureStatus } from "@Services/axiosShared";
import { STATUS_DOCUMENT_PATH } from "./platformStatusModel";
import { createPlatformStatusMonitor } from "./platformStatusMonitor";
import { sha256Hex, takeBypassParam } from "./staffBypass";

const BYPASS_STORAGE_KEY = "maintenanceBypass";

function readBypassToken(): string | null {
  try {
    return localStorage.getItem(BYPASS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export const webPlatformStatus = createPlatformStatusMonitor({
  // Same origin as the app, so every portal host reads its own deployment's document.
  fetchStatusDocument: (signal) => fetch(STATUS_DOCUMENT_PATH, { cache: "no-cache", credentials: "omit", signal }),
  // `/` is what the load balancer's own health check requests, so it is always cheap to answer.
  probeBackend: async (signal) => {
    const res = await fetch(`${getBackendUrl()}/`, { cache: "no-store", credentials: "omit", signal });
    return !isInfraFailureStatus(res.status);
  },
  isDeviceOffline: () => !navigator.onLine,
  isForeground: () => document.visibilityState === "visible",
  watchLifecycle: ({ onForeground, onReconnect }) => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onForeground();
    };
    window.addEventListener("online", onReconnect);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("online", onReconnect);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  },
  loadBypassTokenHash: async () => {
    const token = readBypassToken();
    return token ? sha256Hex(token) : null;
  },
});

/**
 * Stores or clears a staff bypass token passed as `?maintenance-bypass=…`, and takes it out of the
 * address bar so it is not shared by copying the URL. Runs before the router reads the location.
 */
export function captureMaintenanceBypass() {
  const { token, search } = takeBypassParam(window.location.search);
  if (token === undefined) return;
  try {
    if (token === null) localStorage.removeItem(BYPASS_STORAGE_KEY);
    else localStorage.setItem(BYPASS_STORAGE_KEY, token);
  } catch {
    // Storage blocked (private mode, policy): the bypass simply does not stick.
  }
  window.history.replaceState(window.history.state, "", `${window.location.pathname}${search}${window.location.hash}`);
}
